import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  Filter, 
  Grid3X3, 
  List, 
  Clock, 
  Eye, 
  Heart, 
  BookOpen,
  Star,
  // TrendingUp, // removed unused
  // Calendar, // removed unused
  Tag,
  ChevronDown,
  X
} from 'lucide-react';
import { RootState } from '../store';
import {
  setSelectedCategory,
  setSearchQuery,
  setSortBy,
  setSortOrder,
  setViewMode,
  incrementViewCount,
  toggleLike
} from '../store/slices/summariesSlice';
import {
  fetchSummaryCards,
  incrementViewCount as incrementSummaryViewCount,
  toggleLikeSummaryCard
} from '../store/slices/summarySlice';

import { LGS_SUBJECTS } from '../constants/lgsSubjects';
import { SummaryCard } from '../types';
import DOMPurify from 'dompurify';
import { formatFractions } from '../utils/fractionFormatter';

const Summaries: React.FC = () => {
  const dispatch = useDispatch();
  const {
    cards: legacyCards,
    selectedCategory,
    searchQuery,
    sortBy,
    sortOrder,
    viewMode,
    isLoading: legacyLoading
  } = useSelector((state: RootState) => state.summaries);
  
  const {
    cards: newCards,
    isLoading: newLoading
  } = useSelector((state: RootState) => state.summary);
  
  // Only use cards from database (Firebase)
  const cards = newCards.filter(card => card.isPublished);
  const isLoading = newLoading;

  const [selectedCard, setSelectedCard] = useState<SummaryCard | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  // Calculate dynamic categories based on all cards (legacy + new)
  const dynamicCategories = React.useMemo(() => {
    const categoryMap = new Map();
    
    // Initialize with LGS subjects
    LGS_SUBJECTS.forEach(subject => {
      categoryMap.set(subject.value, {
        id: subject.value,
        name: subject.label,
        description: `${subject.label} konularına ait özet kartları`,
        icon: 'BookOpen',
        color: subject.color || '#3B82F6',
        cardCount: 0,
        isActive: true
      });
    });
    
    // Count cards for each category
    cards.forEach(card => {
      if (categoryMap.has(card.subject)) {
        const category = categoryMap.get(card.subject);
        category.cardCount += 1;
      }
    });
    
    // Return only categories with cards
    return Array.from(categoryMap.values()).filter(cat => cat.cardCount > 0);
  }, [cards]);

  // Load summary cards from Firebase on component mount
  useEffect(() => {
    dispatch(fetchSummaryCards());
  }, [dispatch]);

  // Filter and sort cards
  const filteredCards: SummaryCard[] = cards
    .filter((card: SummaryCard) => {
      const matchesCategory = !selectedCategory || card.subject === selectedCategory;
      const matchesSearch = !searchQuery || 
        card.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        card.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
        card.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesCategory && matchesSearch;
    })
    .sort((a: SummaryCard, b: SummaryCard) => {
      let aValue: any, bValue: any;
      
      switch (sortBy) {
        case 'title':
          aValue = a.title;
          bValue = b.title;
          break;
        case 'subject':
          aValue = a.subject;
          bValue = b.subject;
          break;
        case 'difficulty':
          const difficultyOrder = { 'easy': 1, 'medium': 2, 'hard': 3 };
          aValue = difficultyOrder[a.difficulty as keyof typeof difficultyOrder] || 0;
          bValue = difficultyOrder[b.difficulty as keyof typeof difficultyOrder] || 0;
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt).getTime();
          bValue = new Date(b.createdAt).getTime();
          break;
        case 'viewCount':
          aValue = a.viewCount;
          bValue = b.viewCount;
          break;
        case 'likeCount':
          aValue = a.likeCount;
          bValue = b.likeCount;
          break;
        default:
          aValue = 0;
          bValue = 0;
      }
      
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

  const handleCardClick = (card: SummaryCard) => {
    setSelectedCard(card);
    // Check if it's a new card (has createdBy field) or legacy card
    if (card.createdBy) {
      dispatch(incrementSummaryViewCount(card.id));
    } else {
      dispatch(incrementViewCount(card.id));
    }
  };

  const handleLike = (cardId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    // Find the card to determine if it's new or legacy
    const card = cards.find(c => c.id === cardId);
    if (card?.createdBy) {
      dispatch(toggleLikeSummaryCard({ id: cardId, increment: true }));
    } else {
      dispatch(toggleLike(cardId));
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'hard': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getDifficultyText = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'Kolay';
      case 'medium': return 'Orta';
      case 'hard': return 'Zor';
      default: return difficulty;
    }
  };

  const getSubjectName = (subject: string) => {
    // Check LGS subjects
    const lgsSubject = LGS_SUBJECTS.find(subj => subj.value === subject);
    return lgsSubject ? lgsSubject.label : subject;
  };

  return (
    <div className="min-h-screen bg-transparent">
      {/* Modern Header */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 rounded-3xl p-8 text-white mb-8">
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="relative z-10">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                  <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">
                    Konu Özetleri
                  </h1>
                  <p className="text-blue-100 text-lg">
                    LGS konularını özetlerle pekiştirin ve başarıya ulaşın
                  </p>
                </div>
                
                {/* Modern Search and Controls */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-blue-200 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Özet ara..."
                      value={searchQuery}
                      onChange={(e) => dispatch(setSearchQuery(e.target.value))}
                      className="w-full pl-12 pr-4 py-3 bg-white/20 backdrop-blur-sm border border-white/30 rounded-2xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent transition-all duration-300"
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowFilters(!showFilters)}
                      className={`group relative overflow-hidden px-6 py-3 rounded-2xl text-sm font-semibold transition-all duration-300 transform hover:scale-105 ${
                        showFilters
                          ? 'bg-white/30 text-white shadow-lg'
                          : 'bg-white/20 text-blue-100 hover:bg-white/30'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Filter className="w-4 h-4" />
                        <span>Filtrele</span>
                        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${
                          showFilters ? 'rotate-180' : ''
                        }`} />
                      </div>
                    </button>
                    
                    <div className="flex bg-white/20 rounded-2xl p-1">
                      <button
                        onClick={() => dispatch(setViewMode('grid'))}
                        className={`p-2 rounded-xl transition-all duration-300 ${
                          viewMode === 'grid'
                            ? 'bg-white/30 text-white shadow-lg'
                            : 'text-blue-200 hover:bg-white/20'
                        }`}
                      >
                        <Grid3X3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => dispatch(setViewMode('list'))}
                        className={`p-2 rounded-xl transition-all duration-300 ${
                          viewMode === 'list'
                            ? 'bg-white/30 text-white shadow-lg'
                            : 'text-blue-200 hover:bg-white/20'
                        }`}
                      >
                        <List className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-white/10 to-transparent rounded-full -translate-y-32 translate-x-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-white/5 to-transparent rounded-full translate-y-24 -translate-x-24"></div>
          </div>
        </div>

        {/* Modern Filters */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0, y: -20 }}
              animate={{ opacity: 1, height: 'auto', y: 0 }}
              exit={{ opacity: 0, height: 0, y: -20 }}
              className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl p-6 shadow-xl border border-white/20 max-w-7xl mx-auto mb-8"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Category Filter */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                    Kategori
                  </label>
                  <select
                    value={selectedCategory || ''}
                    onChange={(e) => dispatch(setSelectedCategory(e.target.value || null))}
                    className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-2xl bg-white/60 dark:bg-gray-700/60 text-gray-900 dark:text-white backdrop-blur-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  >
                    <option value="">Tüm Kategoriler</option>
                    {dynamicCategories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name} ({category.cardCount})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Sort By */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                    Sıralama
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => dispatch(setSortBy(e.target.value as any))}
                    className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-2xl bg-white/60 dark:bg-gray-700/60 text-gray-900 dark:text-white backdrop-blur-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  >
                    <option value="createdAt">Oluşturma Tarihi</option>
                    <option value="title">Başlık</option>
                    <option value="subject">Konu</option>
                    <option value="difficulty">Zorluk</option>
                    <option value="viewCount">Görüntülenme</option>
                    <option value="likeCount">Beğeni</option>
                  </select>
                </div>

                {/* Sort Order */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                    Sıra
                  </label>
                  <select
                    value={sortOrder}
                    onChange={(e) => dispatch(setSortOrder(e.target.value as 'asc' | 'desc'))}
                    className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-2xl bg-white/60 dark:bg-gray-700/60 text-gray-900 dark:text-white backdrop-blur-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  >
                    <option value="desc">Azalan</option>
                    <option value="asc">Artan</option>
                  </select>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Modern Categories */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-wrap gap-3 mb-8">
          <button
            onClick={() => dispatch(setSelectedCategory(null))}
            className={`group relative overflow-hidden px-6 py-3 rounded-2xl text-sm font-semibold transition-all duration-300 transform hover:scale-105 ${
              !selectedCategory
                ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                : 'bg-white/80 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-white dark:hover:bg-gray-700 backdrop-blur-sm'
            }`}
          >
            <span className="relative z-10">Tümü</span>
            {!selectedCategory && (
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            )}
          </button>
          
          {dynamicCategories.map(category => (
            <button
              key={category.id}
              onClick={() => dispatch(setSelectedCategory(category.id))}
              className={`group relative overflow-hidden px-6 py-3 rounded-2xl text-sm font-semibold transition-all duration-300 transform hover:scale-105 ${
                selectedCategory === category.id
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                  : 'bg-white/80 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-white dark:hover:bg-gray-700 backdrop-blur-sm'
              }`}
            >
              <span className="relative z-10">
                {category.name} ({category.cardCount})
              </span>
              {selectedCategory === category.id && (
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              )}
            </button>
          ))}
        </div>

        {/* Modern Card List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-blue-200 dark:border-blue-800 rounded-full animate-spin"></div>
              <div className="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-blue-600 rounded-full animate-spin"></div>
            </div>
          </div>
        ) : filteredCards.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900 rounded-3xl p-12 max-w-md mx-auto">
              <BookOpen className="w-16 h-16 text-blue-400 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Özet bulunamadı
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Aradığınız kriterlere uygun özet kartı bulunamadı.
              </p>
              <button
                onClick={() => {
                  dispatch(setSearchQuery(''));
                  dispatch(setSelectedCategory(null));
                }}
                className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-2xl font-semibold hover:from-blue-600 hover:to-purple-700 transition-all duration-300 transform hover:scale-105"
              >
                Filtreleri Temizle
              </button>
            </div>
          </motion.div>
        ) : (
          <div className={`grid gap-6 ${
            viewMode === 'grid' 
              ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' 
              : 'grid-cols-1'
          }`}>
            {filteredCards.map((card, index) => (
              <motion.div
                key={card.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => handleCardClick(card)}
                className={`group relative overflow-hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer border border-white/20 dark:border-gray-700/50 hover:scale-[1.02] ${
                  viewMode === 'list' ? 'flex gap-6 p-6' : 'p-6'
                }`}
              >
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                {viewMode === 'grid' ? (
                  <div className="relative z-10">
                    {/* Card Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <span 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: card.color }}
                        ></span>
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          {getSubjectName(card.subject)}
                        </span>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getDifficultyColor(card.difficulty)}`}>
                        {getDifficultyText(card.difficulty)}
                      </span>
                    </div>

                    {/* Card Content */}
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">
                      {card.title}
                    </h3>
                    
                    <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
                      {card.topic}
                    </p>

                    {/* Key Points Preview */}
                    {card.keyPoints.length > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Star className="w-4 h-4 text-yellow-500" />
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Önemli Noktalar
                          </span>
                        </div>
                        <ul className="space-y-1">
                          {card.keyPoints.slice(0, 2).map((point, idx) => (
                            <li key={idx} className="text-sm text-gray-600 dark:text-gray-400 flex items-start gap-2">
                              <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                              <span className="line-clamp-1">{point}</span>
                            </li>
                          ))}
                        </ul>
                        {card.keyPoints.length > 2 && (
                          <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                            +{card.keyPoints.length - 2} daha fazla
                          </span>
                        )}
                      </div>
                    )}

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {card.tags.slice(0, 3).map(tag => (
                        <span
                          key={tag}
                          className="px-3 py-1 bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium"
                        >
                          #{tag}
                        </span>
                      ))}
                      {card.tags.length > 3 && (
                        <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full text-xs font-medium">
                          +{card.tags.length - 3}
                        </span>
                      )}
                    </div>

                    {/* Card Footer */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-200/50 dark:border-gray-700/50">
                      <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>{card.estimatedReadTime}dk</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Eye className="w-4 h-4" />
                          <span>{card.viewCount}</span>
                        </div>
                      </div>
                      <button
                        onClick={(e) => handleLike(card.id, e)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-full hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors duration-300"
                      >
                        <Heart className="w-4 h-4" />
                        <span>{card.likeCount}</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="relative z-10 flex-1">
                    {/* List View */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span 
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: card.color }}
                        ></span>
                        <div>
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                            {getSubjectName(card.subject)} • {card.topic}
                          </span>
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">
                            {card.title}
                          </h3>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getDifficultyColor(card.difficulty)}`}>
                        {getDifficultyText(card.difficulty)}
                      </span>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {card.tags.slice(0, 4).map(tag => (
                        <span
                          key={tag}
                          className="px-2 py-1 bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>{card.estimatedReadTime} dakika</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Eye className="w-4 h-4" />
                          <span>{card.viewCount} görüntülenme</span>
                        </div>
                      </div>
                      <button
                        onClick={(e) => handleLike(card.id, e)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-full hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors duration-300"
                      >
                        <Heart className="w-4 h-4" />
                        <span>{card.likeCount}</span>
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Modern Card Detail Modal */}
      <AnimatePresence>
        {selectedCard && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50"
            onClick={() => setSelectedCard(null)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl rounded-2xl sm:rounded-3xl max-w-[95vw] sm:max-w-4xl w-full max-h-[95vh] overflow-y-auto scrollbar-hide shadow-2xl border border-white/20 dark:border-gray-700/50"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modern Modal Header */}
              <div className="sticky top-0 bg-gradient-to-r from-white/90 to-gray-50/90 dark:from-gray-800/90 dark:to-gray-900/90 backdrop-blur-xl border-b border-gray-200/50 dark:border-gray-700/50 p-6 rounded-t-3xl">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: selectedCard.color }}
                      ></span>
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        {getSubjectName(selectedCard.subject)} • {selectedCard.topic}
                      </span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                      {selectedCard.title}
                    </h2>
                    <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        <span>{selectedCard.estimatedReadTime} dakika okuma</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        <span>{selectedCard.viewCount} görüntülenme</span>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(selectedCard.difficulty)}`}>
                        {getDifficultyText(selectedCard.difficulty)}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedCard(null)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                {/* Content */}
                <div className="prose dark:prose-invert max-w-none mb-8">
                  <div 
                    className="text-gray-700 dark:text-gray-300"
                    dangerouslySetInnerHTML={{ 
                      __html: selectedCard.content.replace(/\n/g, '<br>').replace(/#{1,6}\s/g, '<strong>').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') 
                    }}
                  />
                </div>

                {/* Key Points */}
                {selectedCard.keyPoints.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <Star className="w-5 h-5 text-yellow-500" />
                      Önemli Noktalar
                    </h3>
                    <div className="space-y-2">
                      {selectedCard.keyPoints.map((point, index) => (
                        <div key={index} className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                          <span className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                            {index + 1}
                          </span>
                          <span className="text-gray-700 dark:text-gray-300">{point}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Formulas */}
                {selectedCard.formulas && selectedCard.formulas.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Formüller
                    </h3>
                    <div className="space-y-2">
                      {selectedCard.formulas.map((formula, index) => (
                        <div key={index} className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg font-mono text-center">
                          <span
                            className="text-gray-700 dark:text-gray-300"
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatFractions(formula)) }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Examples */}
                {selectedCard.examples && selectedCard.examples.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Örnekler
                    </h3>
                    <div className="space-y-2">
                      {selectedCard.examples.map((example, index) => (
                        <div key={index} className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                          <span className="text-gray-700 dark:text-gray-300">{example}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Important Notes */}
                {selectedCard.importantNotes && selectedCard.importantNotes.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-red-500" />
                      Önemli Notlar
                    </h3>
                    <div className="space-y-2">
                      {selectedCard.importantNotes.map((note, index) => (
                        <div key={index} className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border-l-4 border-red-500">
                          <span className="text-gray-700 dark:text-gray-300">{note}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tips */}
                {selectedCard.tips && selectedCard.tips.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <Star className="w-5 h-5 text-purple-500" />
                      İpuçları
                    </h3>
                    <div className="space-y-2">
                      {selectedCard.tips.map((tip, index) => (
                        <div key={index} className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                          <span className="text-gray-700 dark:text-gray-300">{tip}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Common Mistakes */}
                {selectedCard.commonMistakes && selectedCard.commonMistakes.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <X className="w-5 h-5 text-orange-500" />
                      Sık Yapılan Hatalar
                    </h3>
                    <div className="space-y-2">
                      {selectedCard.commonMistakes.map((mistake, index) => (
                        <div key={index} className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border-l-4 border-orange-500">
                          <span className="text-gray-700 dark:text-gray-300">{mistake}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Related Topics */}
                {selectedCard.relatedTopics && selectedCard.relatedTopics.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-indigo-500" />
                      İlgili Konular
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedCard.relatedTopics.map((topic, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 rounded-full text-sm font-medium"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tags */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Tag className="w-5 h-5" />
                    Etiketler
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedCard.tags.map(tag => (
                      <span
                        key={tag}
                        className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full text-sm"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                    <span>Oluşturulma: {new Date(selectedCard.createdAt).toLocaleDateString('tr-TR')}</span>
                    <span>Güncellenme: {new Date(selectedCard.updatedAt).toLocaleDateString('tr-TR')}</span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLike(selectedCard.id, e);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                  >
                    <Heart className="w-4 h-4" />
                    <span>Beğen ({selectedCard.likeCount})</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Summaries;